import { BattleUnitState, BattleTileState, BattleState, HotbarSkill, Skill, TurnOrderEntry } from '../types/battle';

// .grid-tile's real rendered size changes at these breakpoints (App.css) - single source of
// truth shared by useGridControls' autoFit (GH-60) and BattleFx's grid->pixel conversion (GH-79),
// so projectile/impact FX line up with the grid at every breakpoint, not just desktop.
export const getBattleTileSize = (): number => {
  if (typeof window === 'undefined') return 80;
  const w = window.innerWidth;
  if (w <= 768) return 40;
  if (w <= 1024) return 50;
  return 80;
};

export const getHealthColor = (percentage: number): string => {
  if (percentage > 0.6) return '#4CAF50';
  if (percentage > 0.3) return '#FF9800';
  return '#f44336';
};

export const getSkillEmoji = (skillName: string): string => {
  const skillEmojis: { [key: string]: string } = {
    'Fireball': '🔥',
    'Magic Missile': '✨',
    'Heal': '💚',
    'Mass Heal': '💞',
    'Lightning Bolt': '⚡',
    'Ice Shard': '🧊',
    'Teleport': '🌟',
    'Shield': '🛡️'
  };
  return skillEmojis[skillName] || '✨';
};

export const getSkillDescription = (skillName: string, affectedCount: number = 1): string => {
  const descriptions: { [key: string]: string } = {
    'Fireball': 'hurls a blazing fireball',
    'Magic Missile': 'launches magical missiles',
    'Heal': 'channels healing energy',
    'Mass Heal': 'radiates healing light',
    'Lightning Bolt': 'calls down lightning',
    'Ice Shard': 'conjures razor-sharp ice',
    'Teleport': 'vanishes and reappears elsewhere',
    'Shield': 'conjures a protective barrier'
  };
  return descriptions[skillName] || `casts ${skillName}`;
};

export const getComboCallout = (comboType: string): string => {
  const callouts: { [key: string]: string } = {
    PincerAttack: '⚔️ PINCER!',
    Surround: '💥 SURROUNDED!',
    FocusFire: '🎯 FOCUS FIRE!',
    TagTeam: '👊 TAG TEAM!',
    Ambush: '🗡️ AMBUSH!',
  };
  return callouts[comboType] || '⚔️ COMBO!';
};

export const isUnitRooted = (unit: BattleUnitState): boolean => {
  if (!unit || !unit.statusEffects) return false;
  return unit.statusEffects.some((effect) =>
    effect.effectType === 'Root' && effect.turnsRemaining > 0
  );
};

export const isUnitStunned = (unit: BattleUnitState): boolean => {
  if (!unit || !unit.statusEffects) return false;
  return unit.statusEffects.some((effect) =>
    effect.effectType === 'Stun' && effect.turnsRemaining > 0
  );
};

export const getDisabledReason = (unit: BattleUnitState | null, action: string): string | null => {
  if (!unit) return null;

  const stunnedEffect = unit.statusEffects?.find((e) => e.effectType === 'Stun' && e.turnsRemaining > 0);
  if (stunnedEffect) {
    return `Unit is stunned by ${stunnedEffect.effectName} (${stunnedEffect.turnsRemaining} turns) - cannot ${action}`;
  }

  if (action === 'move') {
    const rootedEffect = unit.statusEffects?.find((e) => e.effectType === 'Root' && e.turnsRemaining > 0);
    if (rootedEffect) {
      return `Unit is rooted by ${rootedEffect.effectName} (${rootedEffect.turnsRemaining} turns) - cannot move`;
    }
  }

  return null;
};

export const calculateAOEPreview = (
  attackerPos: { x: number; y: number },
  targetX: number,
  targetY: number,
  aoeShape: string
): { x: number; y: number }[] => {
  const tiles: { x: number; y: number }[] = [];
  tiles.push({ x: targetX, y: targetY });

  if (!aoeShape || aoeShape === 'Single') {
    return tiles;
  }

  const dx = targetX - attackerPos.x;
  const dy = targetY - attackerPos.y;
  const dirX = dx !== 0 ? dx / Math.abs(dx) : 0;
  const dirY = dy !== 0 ? dy / Math.abs(dy) : 0;

  switch (aoeShape) {
    case 'Lance':
      tiles.push({ x: targetX + dirX, y: targetY + dirY });
      tiles.push({ x: targetX + dirX * 2, y: targetY + dirY * 2 });
      break;

    case 'Cleave':
      if (dirX !== 0 && dirY === 0) {
        tiles.push({ x: targetX, y: targetY - 1 });
        tiles.push({ x: targetX, y: targetY + 1 });
      } else if (dirX === 0 && dirY !== 0) {
        tiles.push({ x: targetX - 1, y: targetY });
        tiles.push({ x: targetX + 1, y: targetY });
      } else {
        tiles.push({ x: targetX + dirY, y: targetY + dirX });
        tiles.push({ x: targetX - dirY, y: targetY - dirX });
      }
      break;

    case 'Cone':
      for (let x = -1; x <= 1; x++) {
        for (let y = -1; y <= 1; y++) {
          if (x === 0 && y === 0) continue;
          tiles.push({ x: targetX + x, y: targetY + y });
        }
      }
      break;

    case 'Circle':
      for (let x = -1; x <= 1; x++) {
        for (let y = -1; y <= 1; y++) {
          if (x === 0 && y === 0) continue;
          tiles.push({ x: targetX + x, y: targetY + y });
        }
      }
      break;
  }

  return tiles;
};

export const getTileColor = (tile: BattleTileState): string => {
  if (!tile) return '#90EE90';
  if (tile.hazardType) {
    switch (tile.hazardType) {
      case 'Fire':      return '#FF4500';
      case 'Poison':    return '#9ACD32';
      case 'Ice':       return '#00CED1';
      case 'Lightning': return '#FFD700';
      case 'Acid':      return '#ADFF2F';
      case 'Thorns':    return '#8B4513';
      case 'Miasma':    return '#4B0082';
    }
  }
  switch (tile.type) {
    case 'Normal':     return '#90EE90';
    case 'Grass':      return '#7CFC00';
    case 'Forest':     return '#228B22';
    case 'Mountain':   return '#8B7355';
    case 'Water':      return '#87CEEB';
    case 'DeepWater':  return '#4682B4';
    case 'Sand':       return '#F4A460';
    case 'Snow':       return '#F0F8FF';
    case 'Swamp':      return '#556B2F';
    case 'Road':       return '#A9A9A9';
    case 'Bridge':     return '#8B4513';
    case 'Lava':       return '#FF6347';
    case 'Pit':        return '#2F4F4F';
    case 'HighGround': return '#D2B48C';
    case 'Rock':       return '#A0522D';
    case 'Ice':        return '#B0E0E6';
    case 'Rubble':     return '#808080';
    case 'Difficult':  return '#DEB887';
    case 'Impassable': return '#696969';
    default:           return '#90EE90';
  }
};

export const getUnitAtPosition = (units: BattleUnitState[], x: number, y: number): BattleUnitState | undefined =>
  units.find((unit) => unit.position.x === x && unit.position.y === y);

export const getTileAtPosition = (tiles: BattleTileState[], x: number, y: number): BattleTileState | undefined =>
  tiles.find((tile) => tile.x === x && tile.y === y);

// Client-side path estimate for optimistic movement animation only (GH-53). The server doesn't
// return a step-by-step path for player moves, so we BFS over the already-fetched validMoves
// reachable set (which already accounts for terrain/occupancy up to the unit's move range) to
// build a plausible hop sequence. Purely presentational - server state always reconciles after.
export const computeMovementPath = (
  start: { x: number; y: number },
  target: { x: number; y: number },
  validMoves: { x: number; y: number }[]
): { x: number; y: number }[] => {
  const key = (p: { x: number; y: number }) => `${p.x},${p.y}`;
  const passable = new Set(validMoves.map(key));
  passable.add(key(start));
  if (!passable.has(key(target))) return [target];

  const queue: { x: number; y: number }[] = [start];
  const cameFrom = new Map<string, { x: number; y: number }>();
  const visited = new Set<string>([key(start)]);
  let found = false;

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current.x === target.x && current.y === target.y) { found = true; break; }
    const neighbors = [
      { x: current.x + 1, y: current.y }, { x: current.x - 1, y: current.y },
      { x: current.x, y: current.y + 1 }, { x: current.x, y: current.y - 1 },
    ];
    for (const n of neighbors) {
      const k = key(n);
      if (!passable.has(k) || visited.has(k)) continue;
      visited.add(k);
      cameFrom.set(k, current);
      queue.push(n);
    }
  }
  if (!found) return [target];

  const path: { x: number; y: number }[] = [];
  let cur: { x: number; y: number } | undefined = target;
  while (cur && !(cur.x === start.x && cur.y === start.y)) {
    path.unshift(cur);
    cur = cameFrom.get(key(cur));
  }
  return path;
};

export const calculateValidTargets = (unit: BattleUnitState, battleUnits: BattleUnitState[]): BattleUnitState[] => {
  if (!unit || !unit.isPlayerUnit || unit.stats.currentAP <= 0) return [];
  const weapon = unit.equipment?.weapon;
  const minRange = weapon?.minRange || 1;
  const maxRange = weapon?.maxRange || 1;
  return battleUnits.filter((target) => {
    if (target.isPlayerUnit) return false;
    const distance = Math.abs(unit.position.x - target.position.x) + Math.abs(unit.position.y - target.position.y);
    return distance >= minRange && distance <= maxRange;
  });
};

export const calculateWeaponRange = (unit: BattleUnitState, battle: BattleState): { x: number; y: number }[] => {
  if (!unit || !battle) return [];
  const weapon = unit.equipment?.weapon;
  const minRange = weapon?.minRange || 1;
  const maxRange = weapon?.maxRange || 1;
  const gridWidth = battle.gridWidth || 12;
  const gridHeight = battle.gridHeight || 8;
  const tiles: { x: number; y: number }[] = [];
  for (let x = 0; x < gridWidth; x++) {
    for (let y = 0; y < gridHeight; y++) {
      const distance = Math.abs(unit.position.x - x) + Math.abs(unit.position.y - y);
      if (distance >= minRange && distance <= maxRange) tiles.push({ x, y });
    }
  }
  return tiles;
};

// Enemy threat-range overlay (GH-69): every tile a given enemy could move to and then attack
// from on its next turn, computed client-side from data already in the battle payload. The
// reachable-tile BFS mirrors the server's valid-moves pathfinding (BattleMovementService.
// CalculateValidMovesWithPathfinding) - passable + unoccupied tiles, step count capped at the
// movement budget - but is keyed off maxMovement rather than currentMovement since this always
// previews the unit's *next* turn, by which point its movement will have reset to full.
export const calculateThreatRange = (
  unit: BattleUnitState,
  battle: BattleState
): { x: number; y: number }[] => {
  if (!unit || !battle) return [];
  const gridWidth = battle.gridWidth || 10;
  const gridHeight = battle.gridHeight || 10;
  const movement = unit.stats.maxMovement || 0;

  const isBlocked = (x: number, y: number): boolean => {
    const tile = getTileAtPosition(battle.tiles ?? [], x, y);
    if (!tile || !tile.isPassable) return true;
    const occupant = getUnitAtPosition(battle.units ?? [], x, y);
    return !!(occupant && occupant.id !== unit.id && occupant.stats.currentHP > 0);
  };

  const reachable: { x: number; y: number }[] = [{ x: unit.position.x, y: unit.position.y }];
  const visited = new Set<string>([`${unit.position.x},${unit.position.y}`]);
  let frontier = reachable.slice();
  for (let step = 0; step < movement && frontier.length > 0; step++) {
    const next: { x: number; y: number }[] = [];
    for (const { x, y } of frontier) {
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= gridWidth || ny >= gridHeight) continue;
        const key = `${nx},${ny}`;
        if (visited.has(key) || isBlocked(nx, ny)) continue;
        visited.add(key);
        const tile = { x: nx, y: ny };
        reachable.push(tile);
        next.push(tile);
      }
    }
    frontier = next;
  }

  const minRange = unit.monster?.minAttackRange ?? unit.equipment?.weapon?.minRange ?? 1;
  const maxRange = unit.monster?.maxAttackRange ?? unit.equipment?.weapon?.maxRange ?? 1;

  const threatened = new Map<string, { x: number; y: number }>();
  for (const { x: rx, y: ry } of reachable) {
    for (let x = 0; x < gridWidth; x++) {
      for (let y = 0; y < gridHeight; y++) {
        const distance = Math.abs(rx - x) + Math.abs(ry - y);
        if (distance >= minRange && distance <= maxRange) threatened.set(`${x},${y}`, { x, y });
      }
    }
  }
  return Array.from(threatened.values());
};

export const calculateSkillNodeTargets = (
  skill: HotbarSkill,
  selectedUnit: BattleUnitState,
  battle: BattleState
): { targets: { x: number; y: number }[]; rangePositions: { x: number; y: number }[] } => {
  if (!selectedUnit || !battle || !skill) return { targets: [], rangePositions: [] };

  const targets: { x: number; y: number }[] = [];
  const rangePositions: { x: number; y: number }[] = [];
  const maxRange = skill.skill?.maxRange || skill.skill?.range || 5;
  const minRange = skill.skill?.minRange || 0;
  const skillName = skill.skill?.name || '';

  const groundTargetSkills = ['Leap', 'Teleport', 'Shadow Step'];
  const healingSkills = ['Lesser Heal', 'Greater Heal', 'Area Heal', 'Lay on Hands', 'Blessing', 'Divine Protection'];
  const buffSkills = ['Blessing', 'Divine Protection', 'Haste'];
  const swapSkills = ['Switch Places'];
  const pushPullSkills = ['Push', 'Pull'];

  const isGroundTarget = groundTargetSkills.includes(skillName);
  const isHealingOrBuff = healingSkills.includes(skillName) || buffSkills.includes(skillName);
  const isSwapSkill = swapSkills.includes(skillName);
  const isPushPull = pushPullSkills.includes(skillName);

  for (let x = 0; x < battle.gridWidth; x++) {
    for (let y = 0; y < battle.gridHeight; y++) {
      const distance = Math.abs(selectedUnit.position.x - x) + Math.abs(selectedUnit.position.y - y);
      const allowSelfTarget = isHealingOrBuff && distance === 0;
      const inRange = distance >= minRange && distance <= maxRange && (distance > 0 || allowSelfTarget);

      if (inRange) {
        if (isGroundTarget) {
          const unitAtPos = getUnitAtPosition(battle.units, x, y);
          const tile = getTileAtPosition(battle.tiles, x, y);
          if (skillName === 'Shadow Step' && !unitAtPos && tile?.isPassable) {
            const isAdjacentToEnemy = battle.units.some((u) => {
              if (u.isPlayerUnit || u.stats.currentHP <= 0) return false;
              return Math.abs(u.position.x - x) + Math.abs(u.position.y - y) === 1;
            });
            if (isAdjacentToEnemy) {
              targets.push({ x, y });
              if (distance > 0) rangePositions.push({ x, y });
            }
          } else if (skillName !== 'Shadow Step' && !unitAtPos && tile?.isPassable) {
            targets.push({ x, y });
            if (distance > 0) rangePositions.push({ x, y });
          }
        } else if (isSwapSkill) {
          if (getUnitAtPosition(battle.units, x, y)) targets.push({ x, y });
        } else if (isPushPull) {
          const u = getUnitAtPosition(battle.units, x, y);
          if (u && !u.isPlayerUnit) targets.push({ x, y });
        } else {
          const u = getUnitAtPosition(battle.units, x, y);
          if (u) {
            if (isHealingOrBuff && u.isPlayerUnit) targets.push({ x, y });
            else if (!isHealingOrBuff && !u.isPlayerUnit) targets.push({ x, y });
          }
        }

        if (!isGroundTarget && distance > 0) rangePositions.push({ x, y });
      }
    }
  }

  return { targets, rangePositions };
};

export const canCastAnySkill = (unit: BattleUnitState, skills: Skill[]): boolean => {
  if (!skills || skills.length === 0) return false;
  return skills.some(skill => unit.stats.currentAP >= skill.apCost);
};

export const getCurrentTurnUnit = (battle: BattleState | null): TurnOrderEntry | null => {
  if (!battle?.turnOrder || battle.turnOrder.length === 0) return null;
  const currentIndex = battle.currentUnitIndex || 0;
  if (currentIndex >= battle.turnOrder.length || currentIndex < 0) return battle.turnOrder[0];
  return battle.turnOrder[currentIndex] || battle.turnOrder[0];
};
